import {
  CheckCircleRounded as PassIcon,
  CloseRounded as CloseIcon,
  ErrorRounded as FailIcon,
  ExpandLessRounded as CollapseIcon,
  ExpandMoreRounded as ExpandIcon,
  InfoOutlined as InfoIcon,
  WarningRounded as WarnIcon,
} from "@mui/icons-material";
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import * as config from "config";
import * as fileUtils from "fileUtils";
import { useRef, useState } from "react";
import {
  usePromotePricingModelWorkbookMutation,
  useValidatePricingModelWorkbookMutation,
} from "services/adminService";

const STATUS_ICONS = {
  pass: { icon: PassIcon, color: "success.main" },
  warn: { icon: WarnIcon, color: "warning.main" },
  fail: { icon: FailIcon, color: "error.main" },
  info: { icon: InfoIcon, color: "text.secondary" },
};

const VERDICTS = {
  supported: { severity: "success", label: "Supported" },
  review: { severity: "warning", label: "Review warnings" },
  unsupported: {
    severity: "error",
    label: "Not supported — fix the failures and re-upload",
  },
};

const errorText = (e) =>
  typeof e === "string"
    ? e
    : // the baseQuery wraps failures as {type, message}; String(e) on that yields "[object Object]"
      // and a {code, description} shape collapsed to "CODE: CODE"
      e?.description ?? e?.message ?? e?.error ?? JSON.stringify(e);

const ChecklistRow = ({ check }) => {
  const [showItems, setShowItems] = useState(
    check.status === "fail" || check.status === "warn"
  );
  const { icon: StatusIcon, color } =
    STATUS_ICONS[check.status] ?? STATUS_ICONS.info;
  const items = check.items ?? [];

  return (
    <Paper variant={"outlined"} sx={{ p: 1 }}>
      <Stack direction={"row"} spacing={1} alignItems={"flex-start"}>
        <StatusIcon fontSize={"small"} sx={{ color, mt: "2px" }} />
        <Stack sx={{ flex: 1 }} spacing={0.5}>
          <Typography variant={"body2"} fontWeight={600}>
            {check.title}
          </Typography>
          {check.detail && (
            <Typography variant={"caption"} color={"text.secondary"}>
              {check.detail}
            </Typography>
          )}
          {items.length > 0 && (
            <>
              <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
                <IconButton
                  icon={showItems ? CollapseIcon : ExpandIcon}
                  onClick={() => setShowItems(!showItems)}
                  scale={"small"}
                />
                <Typography variant={"caption"} color={"text.secondary"}>
                  {`${items.length} item${items.length === 1 ? "" : "s"}`}
                </Typography>
              </Stack>
              {showItems && (
                <Stack spacing={0.25} sx={{ pl: 2 }}>
                  {items.map((item, index) => (
                    <Typography
                      key={index}
                      variant={"caption"}
                      sx={{ fontFamily: "monospace" }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

const WorkbookStructure = ({ sheets, definedNames }) => {
  const [show, setShow] = useState(false);

  const sheetList = sheets ?? [];
  const nameList = Object.entries(definedNames ?? {});
  if (sheetList.length === 0 && nameList.length === 0) return null;

  return (
    <>
      <Stack spacing={1} direction={"row"} alignItems={"center"}>
        <IconButton
          icon={show ? CollapseIcon : ExpandIcon}
          onClick={() => setShow(!show)}
        />
        <Typography style={{ marginLeft: 5 }}>
          {`Workbook structure: ${sheetList.length} sheets, ${nameList.length} defined names`}
        </Typography>
      </Stack>
      {show && (
        <Paper variant={"outlined"} sx={{ p: 1 }}>
          <Stack spacing={0.5}>
            {sheetList.map(({ name, hidden }) => (
              <Typography
                key={name}
                variant={"caption"}
                sx={{ fontFamily: "monospace" }}
              >
                {`${name}${hidden ? " (hidden)" : ""}`}
              </Typography>
            ))}
            {nameList.map(([name, ref]) => (
              <Typography
                key={name}
                variant={"caption"}
                sx={{ fontFamily: "monospace" }}
              >
                {`${name}: ${ref}`}
              </Typography>
            ))}
          </Stack>
        </Paper>
      )}
    </>
  );
};

const WorkbookChecklist = ({ checklist }) => {
  const verdict = VERDICTS[checklist.verdict] ?? VERDICTS.review;
  return (
    <Stack spacing={1}>
      <Alert severity={verdict.severity}>
        <AlertTitle>{verdict.label}</AlertTitle>
        {checklist.fileName}
      </Alert>
      {(checklist.checks ?? []).map((check) => (
        <ChecklistRow key={check.id} check={check} />
      ))}
      <WorkbookStructure
        sheets={checklist.sheets}
        definedNames={checklist.definedNames}
      />
    </Stack>
  );
};

const JsonEditor = ({ label, value, onChange, error, helperText, tooltip }) => (
  <TextField
    type={"text"}
    InputLabelProps={{ shrink: true }}
    InputProps={{
      spellCheck: "false",
      sx: { fontFamily: "monospace" },
      endAdornment: tooltip ? (
        <InputAdornment
          position={"end"}
          sx={{ alignSelf: "flex-start", mt: 2 }}
        >
          <Tooltip title={tooltip} placement={"left"}>
            <InfoIcon fontSize={"small"} color={"action"} />
          </Tooltip>
        </InputAdornment>
      ) : null,
    }}
    label={label}
    autoComplete={"off"}
    variant={"filled"}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    error={error != null}
    helperText={error ?? helperText}
    multiline
    minRows={4}
    fullWidth
  />
);

const PricingModelEditor = ({
  model: inputModel,
  onSave,
  companyId,
  isOpen,
  onClose,
  reason,
  setReason,
}) => {
  const [model, setModel] = useState({
    active: true,
    title: "",
    engine: "",
    ...inputModel,
  });
  const [metricsText, setMetricsText] = useState(
    JSON.stringify(inputModel?.metrics ?? {}, null, 2)
  );
  const [fieldsText, setFieldsText] = useState(
    JSON.stringify(inputModel?.fields ?? [], null, 2)
  );
  const [resultLabelsText, setResultLabelsText] = useState(
    JSON.stringify(inputModel?.resultLabels ?? {}, null, 2)
  );
  const [jsonErrors, setJsonErrors] = useState({});

  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [checklist, setChecklist] = useState(null);

  const [promoteModel, setPromoteModel] = useState("");
  const [promoteVersion, setPromoteVersion] = useState("");
  const [promoteReason, setPromoteReason] = useState("");
  const [promoteError, setPromoteError] = useState(null);
  const [promotedPath, setPromotedPath] = useState(null);

  const fileInputRef = useRef(null);

  const [
    validateWorkbook,
    { isLoading: isValidating },
  ] = useValidatePricingModelWorkbookMutation();
  const [
    promoteWorkbook,
    { isLoading: isPromoting },
  ] = usePromotePricingModelWorkbookMutation();

  const updateModel = (values) => {
    setModel((currentModel) => ({
      ...currentModel,
      ...values,
    }));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setChecklist(null);
    setUploadError(null);
    setPromoteError(null);
    setPromotedPath(null);
    setUploading(true);
    try {
      const newFileId = await fileUtils.createFileEntry(
        file,
        false,
        null,
        companyId
      );
      setFileId(newFileId);
      setUploading(false);
      const result = await validateWorkbook({
        companyId,
        fileId: newFileId,
      }).unwrap();
      setChecklist(result);
    } catch (e) {
      setUploadError(errorText(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handlePromote = async () => {
    setPromoteError(null);
    setPromotedPath(null);
    try {
      const result = await promoteWorkbook({
        companyId,
        fileId,
        model: promoteModel.trim(),
        version: promoteVersion.trim(),
        userLogMessage: promoteReason,
      }).unwrap();
      setChecklist(result);
      if (result.promoted) {
        updateModel({
          workbook: { model_path: result.modelPath, sha256: result.sha256 },
        });
        setPromotedPath(result.modelPath);
      } else {
        setPromoteError(
          "Workbook was not promoted — review the checklist above."
        );
      }
    } catch (e) {
      setPromoteError(errorText(e));
    }
  };

  const seedFields = () => {
    let current;
    try {
      current = JSON.parse(fieldsText);
    } catch (e) {
      setJsonErrors((errors) => ({
        ...errors,
        fields: `Fix the fields JSON before seeding: ${e.message}`,
      }));
      return;
    }
    // Spread the candidate through rather than cherry-picking: the backend derives min/max/
    // integer/exclusiveMin/exclusiveMax from the workbook's own data validations, and those are
    // the only thing the client-side number validation checks.
    const existing = Array.isArray(current) ? current : [];
    const taken = new Set(existing.map((field) => field?.key));
    const seeded = (checklist?.candidateFields ?? [])
      .map(({ cells, optionsUnresolved, ...field }) => ({
        ...field,
        ...(field.cell == null && cells != null ? { cell: cells } : {}),
      }))
      // seeding twice must not duplicate every field into duplicate React keys
      .filter((field) => !taken.has(field.key));
    setFieldsText(JSON.stringify([...existing, ...seeded], null, 2));
    setJsonErrors(({ fields, ...rest }) => rest);
  };

  const handleSave = () => {
    const errors = {};
    const parsed = {};
    const parse = (key, text) => {
      try {
        parsed[key] = JSON.parse(text);
      } catch (e) {
        errors[key] = e.message;
      }
    };
    parse("metrics", metricsText);
    parse("fields", fieldsText);
    parse("resultLabels", resultLabelsText);
    // registry.PricingModel requires both, and load_models logs-and-skips an entry that fails to
    // validate — so without this the model looks saved here but does not exist anywhere else:
    // no tab, and "Engine not found" from both engine endpoints.
    if (!model.engine?.trim()) {
      errors.engine =
        "An engine name is required, or the model is ignored by the backend.";
    }
    if (!model.workbook?.model_path) {
      errors.workbook =
        "Promote a workbook first — an entry without one is ignored by the backend.";
    }
    setJsonErrors(errors);
    if (Object.keys(errors).length > 0) return;
    onSave({
      model: { ...model, ...parsed },
      userLogMessage: reason,
    });
  };

  const promoteDisabled =
    fileId == null ||
    checklist == null ||
    checklist.verdict === "unsupported" ||
    !promoteModel.trim() ||
    !promoteVersion.trim() ||
    (config.ENVIRONMENT !== "dev" && promoteReason?.length < 4);

  return (
    <Dialog
      fullWidth
      maxWidth={"md"}
      sx={{ "& .MuiDialog-paper": { maxWidth: 980, width: 980 } }}
      open={isOpen}
    >
      <DialogContent>
        <Component
          title={model.title}
          options={<IconButton icon={CloseIcon} onClick={onClose} />}
        >
          <Stack direction={"column"} gap={2}>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Typography>
                {model.id ? `Id: ${model.id}` : "New Pricing Model"}
              </Typography>
              <FormControlLabel
                label={"Active"}
                labelPlacement={"start"}
                control={
                  <Switch
                    checked={model.active ?? false}
                    onChange={(e) => updateModel({ active: e.target.checked })}
                  />
                }
              />
            </Stack>
            <TextField
              type={"text"}
              InputLabelProps={{ shrink: true }}
              InputProps={{ spellCheck: "false" }}
              label={"Title"}
              autoComplete={"off"}
              variant={"filled"}
              value={model.title ?? ""}
              onChange={(e) => updateModel({ title: e.target.value })}
              fullWidth
            />
            <TextField
              type={"text"}
              InputLabelProps={{ shrink: true }}
              InputProps={{ spellCheck: "false" }}
              label={"Engine"}
              autoComplete={"off"}
              variant={"filled"}
              value={model.engine ?? ""}
              onChange={(e) => updateModel({ engine: e.target.value })}
              helperText={
                "Must be unique — referenced by analytics config programPricing.engine. Convention: xl_<client>_<line>"
              }
              fullWidth
            />
            <Typography variant={"subtitle2"}>{"Workbook"}</Typography>
            {model.workbook?.model_path && (
              <Paper variant={"outlined"} sx={{ p: 1 }}>
                <Stack spacing={0.5}>
                  <Typography variant={"caption"} color={"text.secondary"}>
                    {"Current workbook"}
                  </Typography>
                  <Typography
                    variant={"body2"}
                    sx={{ fontFamily: "monospace" }}
                  >
                    {model.workbook.model_path}
                  </Typography>
                  <Typography
                    variant={"caption"}
                    color={"text.secondary"}
                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {`sha256: ${model.workbook.sha256}`}
                  </Typography>
                </Stack>
              </Paper>
            )}
            <input
              type={"file"}
              accept={".xlsx,.xlsm,.xlsb"}
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <Stack direction={"row"} spacing={2} alignItems={"center"}>
              <Button
                onClick={() => fileInputRef.current?.click?.()}
                isLoading={uploading || isValidating}
              >
                {model.workbook?.model_path
                  ? "Upload New Workbook"
                  : "Upload Workbook"}
              </Button>
              {fileId && (
                <Typography variant={"caption"} color={"text.secondary"}>
                  {`File Id: ${fileId}`}
                </Typography>
              )}
            </Stack>
            {uploadError && <Alert severity={"error"}>{uploadError}</Alert>}
            {checklist && <WorkbookChecklist checklist={checklist} />}
            {(checklist?.candidateFields ?? []).length > 0 && (
              <Stack direction={"row"}>
                <Button onClick={seedFields}>
                  {`Seed ${checklist.candidateFields.length} fields from workbook`}
                </Button>
              </Stack>
            )}
            {checklist && (
              <>
                <Typography variant={"subtitle2"}>{"Promote"}</Typography>
                <Stack direction={"row"} spacing={2}>
                  <TextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={"Model"}
                    autoComplete={"off"}
                    variant={"filled"}
                    value={promoteModel}
                    onChange={(e) => setPromoteModel(e.target.value)}
                    helperText={"Lowercase slug, e.g. wolfrum_gl"}
                    fullWidth
                  />
                  <TextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={"Version"}
                    autoComplete={"off"}
                    variant={"filled"}
                    value={promoteVersion}
                    onChange={(e) => setPromoteVersion(e.target.value)}
                    helperText={"e.g. v1 — versions are immutable"}
                    fullWidth
                  />
                </Stack>
                <Stack direction={"row"} spacing={2} alignItems={"flex-start"}>
                  <TextField
                    type={"text"}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ spellCheck: "false" }}
                    label={"Reason for change"}
                    autoComplete={"off"}
                    variant={"filled"}
                    value={promoteReason}
                    onChange={(e) => setPromoteReason(e.target.value)}
                    fullWidth
                  />
                  <Button
                    onClick={handlePromote}
                    isLoading={isPromoting}
                    isDisabled={promoteDisabled}
                  >
                    {"Promote"}
                  </Button>
                </Stack>
                {promoteError && (
                  <Alert severity={"error"}>{promoteError}</Alert>
                )}
                {promotedPath && (
                  <Alert severity={"success"}>
                    {`Workbook promoted to ${promotedPath}. Save the pricing model to apply.`}
                  </Alert>
                )}
              </>
            )}
            <JsonEditor
              label={"Metrics"}
              value={metricsText}
              onChange={setMetricsText}
              error={jsonErrors.metrics}
              helperText={
                "JSON object keyed by metric name, e.g. program_pricing (mapping schema)"
              }
              tooltip={
                "What the engine does with the workbook: which submission values are written " +
                "into which cells, and which cells are read back as results. Client users never " +
                "see this. It reads a value captured by Fields as $.programPricing.inputs.<key>, " +
                "so a field only reaches the workbook once an input here points at it."
              }
            />
            <JsonEditor
              label={"Fields"}
              value={fieldsText}
              onChange={setFieldsText}
              error={jsonErrors.fields}
              helperText={"JSON array of input fields rendered to client users"}
              tooltip={
                "The form the client fills in on this model's pricing tab. Each field's key " +
                "stores its value under programPricing.inputs.<engine>.<key>, which Metrics then " +
                "reads. Adding a field here changes no price on its own, and Metrics can read " +
                "submission data that has no field at all."
              }
            />
            <JsonEditor
              label={"Result Labels"}
              value={resultLabelsText}
              onChange={setResultLabelsText}
              error={jsonErrors.resultLabels}
              helperText={"JSON object mapping output name to display label"}
              tooltip={
                "Display names for the results. Each key must match an output name in " +
                "Metrics — outputs.values or a records column. Anything left out is shown as a " +
                "prettified version of its key."
              }
            />
          </Stack>
        </Component>
      </DialogContent>
      {(jsonErrors.engine || jsonErrors.workbook) && (
        <Alert severity={"error"} sx={{ mx: 3 }}>
          {[jsonErrors.engine, jsonErrors.workbook].filter(Boolean).join(" ")}
        </Alert>
      )}
      <DialogActions>
        <Stack
          direction={"row"}
          gap={2}
          alignItems={"center"}
          justifyContent={"right"}
          sx={{ width: "100%" }}
        >
          <TextField
            type={"text"}
            InputLabelProps={{ shrink: true }}
            InputProps={{ spellCheck: "false" }}
            label={"Reason for change"}
            autoComplete={"off"}
            variant={"filled"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
          />
          <Button
            onClick={handleSave}
            isDisabled={config.ENVIRONMENT !== "dev" && reason?.length < 4}
          >
            {model.id ? "Save" : "Create"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default PricingModelEditor;
